import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { commissionTransfersApi, type CommissionTransfer } from '../../api/commission-transfers.api.js';

import { centsLabel, dateTimeUtc } from './transfer-format.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props { transfer: CommissionTransfer; onClose: () => void }

/** ADMIN only (the caller decides). Settles the fate of an EXECUTION_UNKNOWN transfer from the GuruPay statement. */
export function ResolveTransferDialog({ transfer, onClose }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<'SETTLED' | 'FAILED'>('SETTLED');
  const [reference, setReference] = useState(transfer.providerTransactionUuid ?? '');
  const [reason, setReason] = useState('');
  const resolve = useMutation({
    mutationFn: () => commissionTransfersApi.resolve(transfer.id, {
      outcome, reason: reason.trim(), ...(outcome === 'SETTLED' ? { providerTransactionUuid: reference.trim() } : {}),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['commission-transfers'] });
      void queryClient.invalidateQueries({ queryKey: ['client-money-position'] });
      onClose();
    },
  });
  const referenceOk = outcome === 'FAILED' || UUID.test(reference.trim());
  const valid = referenceOk && reason.trim().length >= 10;
  return <Dialog open onClose={() => { if (!resolve.isPending) onClose(); }} fullWidth maxWidth="sm">
    <DialogTitle>Resolve transfer with unknown outcome</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <DialogContentText>
          {centsLabel(transfer.amountCents, transfer.currency)}, started {dateTimeUtc(transfer.createdAt)}, {transfer.itemCount} commissions.
          Check the GuruPay statement of the source account first. Its end-to-end id is {transfer.id.replace(/-/g, '')}.
        </DialogContentText>
        <Alert severity="warning">
          Choose “Not booked” only if the money never left. Its commissions then return to pending and the next run transfers them again.
        </Alert>
        <TextField select size="small" label="What the statement shows" value={outcome}
          onChange={(event) => setOutcome(event.target.value === 'FAILED' ? 'FAILED' : 'SETTLED')}>
          <MenuItem value="SETTLED">Booked — the transfer happened</MenuItem>
          <MenuItem value="FAILED">Not booked — the money never left</MenuItem>
        </TextField>
        {outcome === 'SETTLED' && <TextField size="small" label="GuruPay transaction reference (UUID)" value={reference}
          onChange={(event) => setReference(event.target.value)} error={reference !== '' && !referenceOk}
          helperText={reference !== '' && !referenceOk ? 'Paste the transaction UUID from GuruPay.' : ' '} />}
        <TextField size="small" label="Reason (recorded in the audit log)" value={reason} multiline minRows={2}
          onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 500 }}
          helperText="At least 10 characters, e.g. where on the statement you found it." />
        {resolve.isError && <Alert severity="error">{resolve.error instanceof Error ? resolve.error.message : 'The transfer could not be resolved.'}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={resolve.isPending}>Cancel</Button>
      <Button variant="contained" color={outcome === 'FAILED' ? 'error' : 'primary'} disabled={!valid || resolve.isPending}
        onClick={() => resolve.mutate()}>{resolve.isPending ? 'Saving…' : 'Resolve'}</Button>
    </DialogActions>
  </Dialog>;
}
